<template>
  <div class="announcement-category-container">
    <Card>
      <p slot="title">公告分类管理</p>

      <!-- 搜索区域 -->
      <div class="search-con">
        <Input
          v-model="searchParams.name"
          placeholder="请输入分类名称"
          style="width: 300px; margin-right: 10px"
          clearable
          @on-enter="handleSearch"
        >
          <span slot="prepend">搜索</span>
        </Input>

        <Select
          v-model="searchParams.status"
          placeholder="请选择状态"
          style="width: 150px; margin-right: 10px"
          clearable
        >
          <Option value="1">启用</Option>
          <Option value="0">禁用</Option>
        </Select>

        <Button type="primary" @click="handleSearch">搜索</Button>
        <Button type="success" @click="handleAdd" style="margin-left: 10px">新增分类</Button>
      </div>

      <!-- 分类列表 -->
      <div style="margin-top: 20px">
        <Table
          :columns="columns"
          :data="tableData"
          :loading="loading"
          border
          stripe
          @on-sort-change="handleSortChange"
        >
          <template slot-scope="{ row }" slot="action">
            <Button type="primary" size="small" @click="handleEdit(row)">编辑</Button>
            <Button
              type="warning"
              size="small"
              style="margin-left: 10px"
              @click="handleStatus(row)"
            >
              {{ row.status === 1 ? '禁用' : '启用' }}
            </Button>
            <Button
              type="error"
              size="small"
              style="margin-left: 10px"
              @click="handleDelete(row)"
            >
              删除
            </Button>
          </template>

          <template slot-scope="{ row }" slot="status">
            <Tag :type="row.status === 1 ? 'success' : 'error'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </Tag>
          </template>
        </Table>

        <!-- 分页 -->
        <div style="margin-top: 20px; text-align: right">
          <Page
            :total="total"
            :current="pageParams.page"
            :page-size="pageParams.limit"
            @on-change="handlePageChange"
            @on-page-size-change="handlePageSizeChange"
            show-sizer
            show-total
          />
        </div>
      </div>
    </Card>

    <!-- 新增/编辑弹窗 -->
    <Modal
      v-model="modalVisible"
      :title="modalTitle"
      width="500px"
      @on-ok="handleSubmit"
      @on-cancel="modalVisible = false"
    >
      <Form ref="categoryForm" :model="formData" :rules="formRules">
        <FormItem label="分类名称" prop="name">
          <Input v-model="formData.name" placeholder="请输入分类名称" />
        </FormItem>

        <FormItem label="排序" prop="sort_order">
          <InputNumber
            v-model="formData.sort_order"
            :min="0"
            :max="100"
            placeholder="请输入排序号"
            style="width: 100%"
          />
        </FormItem>

        <FormItem label="状态" prop="status">
          <Select v-model="formData.status">
            <Option value="1">启用</Option>
            <Option value="0">禁用</Option>
          </Select>
        </FormItem>
      </Form>

      <div slot="footer">
        <Button @click="modalVisible = false">取消</Button>
        <Button type="primary" @click="handleSubmit">确定</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {
  getAnnouncementCategories,
  addAnnouncementCategory,
  updateAnnouncementCategory,
  deleteAnnouncementCategory
} from '@/api/announcement.js'

export default {
  name: 'AnnouncementCategory',
  data () {
    return {
      loading: false,
      tableData: [],
      total: 0,

      searchParams: {
        name: '',
        status: ''
      },

      pageParams: {
        page: 1,
        limit: 10
      },

      modalVisible: false,
      modalTitle: '',
      formData: {
        id: null,
        name: '',
        sort_order: 0,
        status: 1
      },

      formRules: {
        name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        sort_order: [
          { required: true,
message: '请输入排序号',
trigger: 'blur',
validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback(new Error('请输入排序号'))
              } else {
                callback()
              }
            }
          },
          { type: 'number', min: 0, max: 100, message: '排序号必须在 0 到 100 之间', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },

      columns: [
        { title: 'ID', key: 'id', width: 80, sortable: true },
        { title: '分类名称', key: 'name', width: 200, sortable: true },
        { title: '排序', key: 'sort_order', width: 100, sortable: true },
        { title: '状态', slot: 'status', width: 100 },
        { title: '创建时间', key: 'create_time', width: 180, sortable: true },
        { title: '更新时间', key: 'update_time', width: 180, sortable: true },
        { title: '操作', slot: 'action', width: 250, fixed: 'right' }
      ]
    }
  },

  mounted () {
    this.getCategoryList()
  },

  methods: {
    async getCategoryList () {
      this.loading = true
      try {
        const res = await getAnnouncementCategories()
        if (res.data.code === 0) {
          let data = res.data.data

          // 搜索过滤
          if (this.searchParams.name) {
            data = data.filter(item =>
              item.name.includes(this.searchParams.name)
            )
          }

          if (this.searchParams.status !== '') {
            data = data.filter(item =>
              item.status.toString() === this.searchParams.status
            )
          }

          // 分页
          const start = (this.pageParams.page - 1) * this.pageParams.limit
          const end = start + this.pageParams.limit
          this.tableData = data.slice(start, end)
          this.total = data.length
        } else {
          this.$Message.error(res.data.msg || '获取分类列表失败')
        }
      } catch (error) {
        this.$Message.error('获取分类列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    handleSearch () {
      this.pageParams.page = 1
      this.getCategoryList()
    },

    handleSortChange ({ column, key, order }) {
      // 排序功能实现
      if (order === 'asc') {
        this.tableData.sort((a, b) => a[key] > b[key] ? 1 : -1)
      } else if (order === 'desc') {
        this.tableData.sort((a, b) => a[key] < b[key] ? 1 : -1)
      }
    },

    handlePageChange (page) {
      this.pageParams.page = page
      this.getCategoryList()
    },

    handlePageSizeChange (pageSize) {
      this.pageParams.limit = pageSize
      this.pageParams.page = 1
      this.getCategoryList()
    },

    handleAdd () {
      this.modalTitle = '新增分类'
      this.formData = {
        id: null,
        name: '',
        sort_order: 0,
        status: 1
      }
      this.modalVisible = true
    },

    handleEdit (row) {
      this.modalTitle = '编辑分类'
      this.formData = { ...row }
      this.modalVisible = true
    },

    handleStatus (row) {
      this.$Modal.confirm({
        title: '确认操作',
        content: `确定要${row.status === 1 ? '禁用' : '启用'}该分类吗？`,
        onOk: async () => {
          try {
            const res = await updateAnnouncementCategory({
              ...row,
              status: row.status === 1 ? 0 : 1
            })

            if (res.data.code === 0) {
              this.$Message.success('操作成功')
              this.getCategoryList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          } catch (error) {
            this.$Message.error('操作失败')
            console.error(error)
          }
        }
      })
    },

    handleDelete (row) {
      this.$Modal.confirm({
        title: '确认删除',
        content: '确定要删除该分类吗？删除后该分类下的公告将无法正常显示。',
        onOk: async () => {
          try {
            const res = await deleteAnnouncementCategory(row.id)

            if (res.data.code === 0) {
              this.$Message.success('删除成功')
              this.getCategoryList()
            } else {
              this.$Message.error(res.data.msg || '删除失败')
            }
          } catch (error) {
            this.$Message.error('删除失败')
            console.error(error)
          }
        }
      })
    },

    async handleSubmit () {
      try {
        this.$refs.categoryForm.validate(async (valid) => {
          if (valid) {
            let res
            if (this.formData.id) {
              res = await updateAnnouncementCategory(this.formData)
            } else {
              res = await addAnnouncementCategory(this.formData)
            }

            if (res.data.code === 0) {
              this.$Message.success(this.formData.id ? '更新成功' : '新增成功')
              this.modalVisible = false
              this.getCategoryList()
            } else {
              this.$Message.error(res.data.msg || '操作失败')
            }
          }
        })
      } catch (error) {
        this.$Message.error('操作失败')
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.announcement-category-container {
  padding: 10px;
}

.search-con {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}
</style>
